import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { AppGlobalContext } from "../../../contexts/AppContext.context";
import { Icons } from "../../../config/MuiIcons.config";
import { AppTheme } from "../../../theme/App.theme";
import CustomTextArea from "../../../components/CustomTextArea/CustomTextArea";
import MUITextField from "../../../components/MUITextField/MUITextField";
import MUIButton from "../../../components/MUIButton/MUIButton";
import testimonialManagementService from "../../../services/TestimonialManagementService";
import MUIInputLabel from "../../../components/MUIInputLabel/MUIInputLabel";
import MUICard from "../../../components/MUICard/MUICard.component";
import PageHeader from "../../../components/PageHeader/PageHeader";
import { CONTENT_STATUS_CONFIG, CONTENT_TYPES_CONFIG } from "../../../config/App.config";
import { SNACKBAR_VARIANT } from "../../../config/Messages.config";
import { useSnackbar } from "../../../contexts/Snackbar.context";

const Testimonial = () => {
  const navigate = useNavigate();
  const { authUserDetails } = useContext(AppGlobalContext);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { ERROR } = SNACKBAR_VARIANT;
  const { showSnackbar } = useSnackbar();

  const defaultName = useMemo(() => {
    const first = authUserDetails?.firstName || "";
    const last = authUserDetails?.lastName || "";
    return [first, last].filter(Boolean).join(" ");
  }, [authUserDetails]);

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      const payload = {
        ...values,
        language: authUserDetails?.preferredLanguage || "English",
        profilePicture: authUserDetails?.profileImage || null,
        type: CONTENT_TYPES_CONFIG.TESTIMONIAL,
        status: CONTENT_STATUS_CONFIG.PUBLISHED,
        isActive: false,
        createdBy: authUserDetails?.uid || null,
      };
      const { statusCode } = await testimonialManagementService.createOne(
        payload
      );
      if (statusCode === 201 || statusCode === 200) {
        setShowSuccess(true);
        formik.handleReset();
      }
    } catch (error) {
      showSnackbar(
        error?.message || "Failed to create testimonial. Please try again.",
        ERROR
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      text: "",
      userName: defaultName || "",
    },
    validationSchema: Yup.object({
      text: Yup.string().trim().required("Please enter your testimonial"),
      userName: Yup.string().trim().required("Please enter your name"),
    }),
    onSubmit: async (values) => {
      handleSubmit(values);
    },
  });

  return (
    <React.Fragment>
      <Dialog
        open={showSuccess}
        disableEscapeKeyDown
        onClose={(e, reason) => {
          if (reason === "backdropClick") {
            return;
          }
          setShowSuccess(false);
        }}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              maxWidth: "386px",
            },
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              mx: "auto",
              borderRadius: "50%",
              backgroundColor: AppTheme.PRIMARY_COLOR,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Icons.Check
              sx={{
                color: AppTheme.PRIMARY_COLOR,
                backgroundColor: AppTheme.PRIMARY_WHITE,
                fontSize: 30,
                borderRadius: "50%",
              }}
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 4 }}>
          <Typography sx={{ fontWeight: 700, mb: 2, fontSize: "22px" }}>
            Thank you for your Response!
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, px: 2 }}
          >
            We’re grateful you chose to share your experience with us.
          </Typography>
          <MUIButton primary fullWidth onClick={() => navigate("/")}>
            Back to Home
          </MUIButton>
        </DialogContent>
      </Dialog>

      <div>
        <PageHeader title={"Testimonial"} />
      </div>
      <MUICard style={{ maxWidth: 800, mx: "auto" }}>
        <div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              formik.handleSubmit();
            }}
            noValidate
          >
            <Box sx={{ mb: 2 }}>
              <MUIInputLabel>Your Testimonial</MUIInputLabel>
              <CustomTextArea
                name="text"
                id="text"
                placeholder="Write how this app helped you in your spiritual or learning journey..."
                value={formik.values.text}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.text && Boolean(formik.errors.text)}
                helperText={formik.touched.text ? formik.errors.text : ""}
                maxLength={250}
                disabled={submitting}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <MUIInputLabel style={{ fontSize: "16px" }}>
                Your Name
              </MUIInputLabel>
              <MUITextField
                name="userName"
                placeholder="Your full name"
                value={formik.values.userName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={submitting}
                error={
                  formik.touched.userName && Boolean(formik.errors.userName)
                }
                helperText={
                  formik.touched.userName ? formik.errors.userName : ""
                }
              />
              <Typography variant="caption" color="text.secondary">
                Note - I consent to the use and public display of my testimonial, including my name, photo, and comment as part of the app's content.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "stretch", sm: "flex-end" },
              }}
            >
              <MUIButton
                primary
                isLoadingButton
                isLoading={submitting}
                type="submit"
              >
                Submit Your Testimonial
              </MUIButton>
            </Box>
          </form>
        </div>
      </MUICard>
    </React.Fragment>
  );
};

export default Testimonial;
